import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { MinimalButton } from '@ovasabi/ui-minimal'
import { from, minimalVars as v, until } from '@ovasabi/ui-minimal/tokens'
import { useEdition } from '../../edition/EditionProvider'
import { ArrowLeftIcon, BrandMark } from '../icons'
import { desktopGutter, paddedMaxWidth } from './layout'

const Style = {
  Header: styled.header<{ $gradient?: string }>`
    position: relative;
    width: 100%;
    box-sizing: border-box;
    background: ${({ $gradient }) => $gradient || 'transparent'};
    color: ${v.color.textPrimary};
    padding: 14px 14px 6px;
    &[data-emphasis='true'] {
      color: ${v.color.textInverse};
      padding-bottom: ${v.space.md};
    }
    ${from('page')} {
      padding: ${v.space.md} ${desktopGutter}px ${v.space.xs};
    }
  `,
  Inner: styled.div`
    width: 100%;
    min-width: 0;
    max-width: calc(${paddedMaxWidth} - ${desktopGutter * 2}px);
    margin: auto;
    display: grid;
    gap: 10px;
    > form {
      max-width: 680px;
    }
  `,
  TopRow: styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${v.space.sm};
    min-width: 0;
    ${until('page')} { flex-wrap: wrap; }
  `,
  Copy: styled.div`
    min-width: 0;
    flex: 1;
    display: grid;
    gap: ${v.space["2xs"]};
    overflow-wrap: anywhere;
  `,
  Title: styled.h1`
    margin: 0;
    font-family: ${v.typography.displayFamily};
    font-size: ${v.typography.h1Size};
    font-weight: ${v.typography.weightBold};
    line-height: ${v.typography.lineHeightTight};
    letter-spacing: -0.02em;
  `,
  Dot: styled.span`
    color: ${v.color.brand};
    [data-emphasis='true'] & {
      color: inherit;
      opacity: 0.6;
    }
  `,
  Subtitle: styled.p`
    margin: 0;
    color: ${v.color.textSecondary};
    font-size: 0.84rem;
    line-height: 1.45;
    max-width: 62ch;
    min-height: 20px;

    [data-emphasis='true'] & {
      color: inherit;
      opacity: 0.85;
    }
  `,
  Brand: styled.div`
    display: flex;
    align-items: center;
    gap: ${v.space.sm};
    min-width: 0;
    > * { min-width: 0; }
  `,
  Logo: styled.img`
    width: 190px;
    height: auto;
    max-width: 100%;
  `,
  Action: styled.div`
    flex-shrink: 0;
    min-width: 0;
    max-width: 100%;
    ${until('page')} {
      flex-basis: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: ${v.space.xs};
    }
  `,
  Back: styled(MinimalButton)`
    && {
      width: 44px;
      min-width: 44px;
      height: 44px;
      padding: 0;
      border-radius: ${v.radius.pill};
    }
  `,
}

export const PageHeaderActionButton = styled(MinimalButton)`
  && {
    color: ${v.color.brand};
    background: ${v.color.bgSurface};
    border-color: ${v.color.borderSubtle};
  }
`

export interface PageHeaderProps {
  title?: ReactNode
  subtitle?: ReactNode
  wordmark?: boolean
  brandMark?: boolean
  accentDot?: boolean
  action?: ReactNode
  gradient?: string
  children?: ReactNode
  onBack?: () => void
}

/** Page titles scroll with content; persistent navigation belongs to the shell. */
export const PageHeader = ({
  title,
  subtitle,
  wordmark = false,
  brandMark = false,
  accentDot = false,
  action,
  gradient,
  children,
  onBack,
}: PageHeaderProps) => {
  const { profile, edition } = useEdition()
  const surface = gradient ?? (edition === 'chowdash' ? profile.headerGradient : undefined)
  return (
    <Style.Header $gradient={surface} data-emphasis={Boolean(surface)}>
      <Style.Inner>
        <Style.TopRow>
          {onBack && (
            <Style.Back variant="secondary" onClick={onBack} aria-label="Go back">
              <ArrowLeftIcon size="md" />
            </Style.Back>
          )}
          <Style.Copy>
            {wordmark ? (
              <Style.Brand>
                {brandMark &&
                  (edition === 'chowdash' ? (
                    <img src="/brand/chowdash-mark.webp" width="44" height="44" alt="" />
                  ) : (
                    <BrandMark size={36} />
                  ))}
                <Style.Title>
                  {edition === 'chowdash' ? (
                    profile.label
                  ) : (
                    <Style.Logo
                      src="/brand/choosechow-wordmark.webp"
                      width="960"
                      height="138"
                      alt="ChooseChow"
                    />
                  )}
                </Style.Title>
              </Style.Brand>
            ) : (
              <Style.Title>
                {title}
                {accentDot && <Style.Dot aria-hidden="true">.</Style.Dot>}
              </Style.Title>
            )}
            {subtitle && <Style.Subtitle>{subtitle}</Style.Subtitle>}
          </Style.Copy>
          {action && <Style.Action>{action}</Style.Action>}
        </Style.TopRow>
        {children}
      </Style.Inner>
    </Style.Header>
  )
}
